import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, FilterIcon } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { servicesColumns, columnsExcel } from '@/pages/parametrizacion/services/utils/servicesColumns.jsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchServices, createService, updateService, deleteService, exportServices } from '@/pages/parametrizacion/services/Services/services.services.jsx';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const ServicesPage = () => {
  const { toast } = useToast();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [plantasOptions, setPlantasOptions] = useState([]);

  // Cargar plantas para los filtros
  const loadPlantas = useCallback(async () => {
    try {
      const { plantasService } = await import('@/pages/parametrizacion/plantas/utils/plantasService');
      const response = await plantasService.getAll();
      const plantas = response?.data || [];
      setPlantasOptions(plantas.map(planta => ({
        value: planta._id || planta.id,
        label: planta.nombre
      })));
    } catch (error) {
      console.error('Error cargando plantas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las plantas.',
        variant: 'destructive'
      });
    }
  }, [toast]);

  const fetchAndSetServices = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const { data, totalRecords } = await fetchServices({ limit: ITEMS_PER_PAGE, offset, query: filters });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudieron obtener los servicios.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);


    

  useEffect(() => {
    fetchAndSetServices();
    loadPlantas();
  }, [fetchAndSetServices, loadPlantas]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Crear columnas con opciones pre-cargadas
  const getServicesColumnsWithOptions = () => {
    return servicesColumns.map(column => {
      if (column.id === 'planta') {
        return {
          ...column,
          options: plantasOptions.map(planta => ({
            value: { id: planta.value, nombre: planta.label },
            label: planta.label
          }))
        };
      }
      return column;
    });
  };

  const handleOpenModal = (mode, item = null) => {
    // Verificar que las opciones estén cargadas antes de abrir el modal
    if (plantasOptions.length === 0) {
      toast({ 
        title: 'Cargando...', 
        description: 'Espera mientras se cargan las opciones necesarias.', 
        variant: 'default' 
      });
      return;
    }
    
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalMode === 'create') {
        await createService(itemData);
        toast({ title: "Éxito", description: "Servicio creado correctamente." });
      } else if (modalMode === 'edit') {
        await updateService(currentItem._id, itemData);
        toast({ title: "Éxito", description: "Servicio actualizado correctamente." });
      }
      handleCloseModal();
      fetchAndSetServices();
    } catch (error) {
      console.error("Error saving service:", error);
      const errorMessage = error.response?.data?.message || error.message || "Ocurrió un problema al guardar el servicio.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (itemToDelete) {
        const response = await deleteService(itemToDelete._id);
        if (response.code === 200) {
          toast({ title: "Servicio eliminado", description: "El servicio fue eliminado correctamente." });
          fetchAndSetServices();
        } else {
          toast({ 
            title: "Error", 
            description: response.message || "Hubo un problema al eliminar el servicio.", 
            variant: "destructive" 
          });
        }
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      toast({ 
        title: "Error", 
        description: error.message || "Hubo un problema al eliminar el servicio.", 
        variant: "destructive" 
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionType, row) => {
    switch (actionType) {
      case 'edit':
        handleOpenModal('edit', row);
        break;
      case 'delete':
        handleDeleteConfirmation(row);
        break;
      default:
        console.warn(`Acción no implementada: ${actionType}`);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const blob = await exportServices(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `servicios_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Éxito", description: "Servicios exportados correctamente." });
    } catch (error) {
      toast({ title: "Error", description: "No se pudieron exportar los servicios.", variant: "destructive" });
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (key) => {
    setFilters(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="container mx-auto p-4"
    >
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl">Servicios</CardTitle>
              <CardDescription>Gestión de servicios</CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={() => setIsDrawerOpen(true)}
                variant="outline" 
                className="flex items-center gap-2"
              >
                <FilterIcon className="h-4 w-4" />
                Filtrar
              </Button>
              <ExportExcelButton onClick={handleExportToExcel} />
              <Button onClick={() => handleOpenModal('create')} className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4" />
                Nuevo Servicio
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
              <AppliedFilters 
                filters={filters} 
                fields={[
                  { id: 'estado', label: 'Estado', type: 'select', options: [
                    { value: 'Activo', label: 'Activo' },
                    { value: 'Inactivo', label: 'Inactivo' },
                  ]},
                  { 
                    id: 'planta.id', 
                    label: 'Planta', 
                    type: 'select',
                    options: plantasOptions
                  },
                ]}
                onRemoveFilter={handleRemoveFilter}
              />
            </div>
          <DataTable
            data={displayedData}
            columns={servicesColumns}
            isLoading={isLoading}
            onAction={handleAction}
            showCreateButton={false}
          />
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
          <FilterDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={[
              {
                id: 'estado', label: 'Estado', type: 'select', options: [
                  { value: 'Activo', label: 'Activo' },
                  { value: 'Inactivo', label: 'Inactivo' },
                ]
              },
              { 
                id: 'planta.id', 
                label: 'Planta', 
                type: 'select',
                options: plantasOptions
              },
            ]}
            initialFilters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterChange}
            initialDay={true}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && plantasOptions.length > 0 && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            fields={getServicesColumnsWithOptions()}
            title={modalMode === 'create' ? 'Crear Nuevo Servicio' : 'Editar Servicio'}
            mode={modalMode}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar este servicio?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el servicio
              "{itemToDelete?.nombre}" de tus registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default ServicesPage;
